import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Typography,
  Divider,
  Tooltip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import PatientRegistration from './PatientRegistration';

const API_URL = process.env.REACT_APP_API_URL;

const AppointmentScheduler = ({ onScheduled, onCancel, patient }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patients, setPatients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatientData, setNewPatientData] = useState(null);
  
  const [formData, setFormData] = useState({
    appointmentTime: new Date(),
    selectedTests: [],
    notes: ''
  });

  const [loadingTests, setLoadingTests] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      searchPatients();
    }
  }, [searchTerm]);

  // Prefill selected patient if prop changes
  useEffect(() => {
    if (patient) {
      setSelectedPatient(patient);
    }
  }, [patient]);

  const fetchInitialData = async () => {
    setLoadingTests(true);
    setFetchError('');
    try {
      const [categoriesResponse, testsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/tests/categories`),
        axios.get(`${API_URL}/api/tests/all`)
      ]);
      setCategories(categoriesResponse.data);
      setTests(testsResponse.data);
    } catch (err) {
      setFetchError('Failed to load test categories or tests.');
    } finally {
      setLoadingTests(false);
    }
  };

  const searchPatients = async () => {
    if (!searchTerm) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/patients/search?searchTerm=${searchTerm}`);
      setPatients(response.data);
    } catch (err) {
      console.error('Error searching patients:', err);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setShowNewPatientForm(false);
    setNewPatientData(null);
  };

  const handleNewPatient = (patientData) => {
    setNewPatientData(patientData);
    setShowNewPatientForm(false);
  };

  const handleTestSelection = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      selectedTests: newValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const requestData = {
        patientId: selectedPatient?.id,
        patientDetails: newPatientData,
        testIds: formData.selectedTests.map(test => test.id),
        appointmentTime: formData.appointmentTime.toISOString().slice(0, 19),
        notes: formData.notes
      };

      await axios.post(`${API_URL}/api/appointments/schedule`, requestData);
      onScheduled();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  const getTestsByCategory = (categoryId) => {
    return tests.filter(test => test.testCategory?.id === categoryId);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Patient Selection Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
              Patient Information
            </Typography>
            
            {!selectedPatient && !newPatientData && (
              <>
                <Autocomplete
                  options={patients}
                  getOptionLabel={(option) => `${option.name} (${option.phone})`}
                  inputValue={searchTerm}
                  onInputChange={(event, newInputValue) => {
                    setSearchTerm(newInputValue);
                    if (newInputValue) {
                      searchPatients();
                    }
                  }}
                  onChange={(event, newValue) => handlePatientSelect(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Patient by Name, Phone, or Email"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.phone} • {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
                
                <Button
                  variant="outlined"
                  onClick={() => setShowNewPatientForm(true)}
                  sx={{ mb: 2 }}
                >
                  Register New Patient
                </Button>
              </>
            )}

            {selectedPatient && (
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Patient: {selectedPatient.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone: {selectedPatient.phone} • Email: {selectedPatient.email}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setSelectedPatient(null)}
                  sx={{ mt: 1 }}
                >
                  Change Patient
                </Button>
              </Box>
            )}

            {newPatientData && (
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  New Patient: {newPatientData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone: {newPatientData.phone} • Email: {newPatientData.email}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setNewPatientData(null)}
                  sx={{ mt: 1 }}
                >
                  Change Patient
                </Button>
              </Box>
            )}

            {showNewPatientForm && (
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                <PatientRegistration
                  onSaved={handleNewPatient}
                  onCancel={() => setShowNewPatientForm(false)}
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Appointment Details Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
              Appointment Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="Appointment Date & Time"
              value={formData.appointmentTime}
              onChange={(date) => setFormData(prev => ({ ...prev, appointmentTime: date }))}
              renderInput={(params) => <TextField {...params} fullWidth required />}
              minDateTime={new Date()}
            />
          </Grid>

          {/* Test Selection Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select Tests
            </Typography>
            {/* Common Search for All Tests */}
            <Autocomplete
              multiple
              options={tests}
              getOptionLabel={(option) => option.name}
              value={formData.selectedTests}
              onChange={(event, newValue) => setFormData(prev => ({ ...prev, selectedTests: newValue }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and Select Tests (All Categories)"
                  placeholder="Type to search tests..."
                  sx={{ mb: 2 }}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontWeight: selected ? 700 : 400 }}>
                  {option.name} {selected && <span style={{ color: '#1976d2', marginLeft: 8 }}>&#10003;</span>}
                  <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>
                    {option.testCategory?.name}
                  </span>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            {/* Category-wise Selection */}
            {loadingTests ? (
              <Box sx={{ my: 2 }}><CircularProgress size={24} /> Loading tests...</Box>
            ) : fetchError ? (
              <Alert severity="error">{fetchError}</Alert>
            ) : categories.length === 0 ? (
              <Alert severity="info">No test categories available.</Alert>
            ) : (
              categories.map((category) => (
                <Box key={category.id} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {category.name}
                  </Typography>
                  {getTestsByCategory(category.id).length === 0 ? (
                    <Alert severity="info">No tests available in this category.</Alert>
                  ) : (
                    <Autocomplete
                      multiple
                      options={getTestsByCategory(category.id)}
                      getOptionLabel={(option) => option.name}
                      value={formData.selectedTests.filter(test => 
                        getTestsByCategory(category.id).some(catTest => catTest.id === test.id)
                      )}
                      onChange={(event, newValue) => {
                        const otherTests = formData.selectedTests.filter(test => 
                          !getTestsByCategory(category.id).some(catTest => catTest.id === test.id)
                        );
                        setFormData(prev => ({
                          ...prev,
                          selectedTests: [...otherTests, ...newValue]
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Select ${category.name}`}
                          placeholder="Choose tests..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                            sx={{ borderRadius: '16px', fontWeight: 600 }}
                          />
                        ))
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  )}
                </Box>
              ))
            )}
            {/* Selected Tests Grouped by Category */}
            {formData.selectedTests.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Selected Tests (Grouped by Category):
                </Typography>
                {categories.map(category => {
                  const testsInCategory = formData.selectedTests.filter(test => test.testCategory?.id === category.id);
                  if (testsInCategory.length === 0) return null;
                  return (
                    <Box key={category.id} sx={{ mb: 1, ml: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{category.name}:</Typography>
                      {testsInCategory.map(test => (
                        <Chip
                          key={test.id}
                          label={test.name}
                          onDelete={() => {
                            setFormData(prev => ({
                              ...prev,
                              selectedTests: prev.selectedTests.filter(t => t.id !== test.id)
                            }));
                          }}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Tooltip
            title={
              !selectedPatient && !newPatientData
                ? 'Select or register a patient'
                : formData.selectedTests.length === 0
                ? 'Select at least one test'
                : ''
            }
            disableHoverListener={selectedPatient || newPatientData && formData.selectedTests.length > 0}
          >
            <span>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || (!selectedPatient && !newPatientData) || formData.selectedTests.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
              >
                Schedule Appointment
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AppointmentScheduler; 